/**
 * IN-1.d — Compliance Review Queue (admin)
 * ────────────────────────────────────────
 * Route: /admin/compliance-review
 * Roles: super_admin, admin, ops_manager
 *
 * First IN-1 UI surface. Soft enforcement only.
 *
 * Reads (canonical):
 *   • getComplianceReviewQueue(contractors, documents) — IN-1.d
 *   • getComplianceStatus(contractor, documents, parent?) — IN-1.b
 *
 * Writes (canonical writers ONLY):
 *   • verifyComplianceDocument / rejectComplianceDocument — IN-1.b
 *   • suspendContractor / reactivateContractor           — IN-1.d
 *
 * Hard rules:
 *   • No dispatch block. No payout execution. No auto-suspension.
 *   • Document ledger sourced from `OnboardingProvider` (IN-2.a).
 *   • No contractor self-service wizard here.
 */

import { useEffect, useMemo, useState } from "react";
import { useOnboarding } from "@/data/contractor/onboarding/OnboardingProvider";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { supabase } from "@/integrations/supabase/client";
import { ContractorService } from "@/services/contractor.service";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Shield, AlertTriangle, Clock, CheckCircle2, Ban, RotateCcw, Inbox, FileWarning } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { useToast } from "@/hooks/use-toast";
import {
  getComplianceReviewQueue,
  REVIEW_QUEUE_BUCKET_ORDER,
  type ReviewQueueBucket,
  type ReviewQueueRow,
} from "@/data/contractor/compliance/expirySweep";
import {
  type ComplianceDocument,
  type ComplianceDocumentType,
} from "@/data/contractor/compliance/complianceDocuments";
import { getOnboardingDocumentsForSession } from "@/data/contractor/onboarding/documents";
import {
  activateInstallerComplianceCrew,
  approveInstallerComplianceDocument,
  getInstallerComplianceDocumentReadUrl,
  rejectInstallerComplianceDocument,
} from "@/data/contractor/onboarding/backend/installerComplianceReview";
import {
  suspendContractor,
  reactivateContractor,
  MIN_SUSPENSION_REASON_LENGTH,
} from "@/data/contractor/compliance/suspension";
import type { Contractor } from "@/data/types";
import {
  defaultPayProfile,
  type ContractorPayProfile,
  type SpecArchetype,
} from "@/data/contractorPayProfile";
import {
  getOnboardingSessionDocSummary,
  type OnboardingKanbanStatus,
} from "@/data/contractor/onboarding/adminReview";
import { getOnboardingSteps } from "@/data/contractor/onboarding/stepRegistry";
import { getCanonicalSessionStatusLabel } from "@/data/contractor/onboarding/lifecycle";
import type {
  OnboardingInviteToken,
  OnboardingSession,
} from "@/data/contractor/onboarding/types";

const MIN_HANDOFF_REASON = 10;
const DEFAULT_INSTALLER_PORTAL_URL = "http://localhost:3000";

function getInstallerPortalInviteRedirect(): string {
  const configuredBase = String(
    import.meta.env.VITE_INSTALLER_PORTAL_BASE_URL ??
      import.meta.env.VITE_INSTALLER_PORTAL_URL ??
      "",
  ).trim();
  const base = configuredBase || DEFAULT_INSTALLER_PORTAL_URL;
  return `${base.replace(/\/+$/, "")}/activate`;
}

function resolveInviteIdentity(session: OnboardingSession | null): {
  email: string;
  fullName: string;
} | null {
  if (!session) return null;
  const email = String(session.draft.contactEmail ?? "").trim().toLowerCase();
  if (!email) return null;
  const fullName = String(session.draft.contactName ?? session.draft.name ?? "").trim();
  return {
    email,
    fullName: fullName || "Installer Applicant",
  };
}

function findSessionContractor(
  session: OnboardingSession,
  contractors: Contractor[],
): Contractor | null {
  if (!session.contractorId) return null;
  return contractors.find((c) => c.id === session.contractorId) ?? null;
}

function compact(value: unknown): string {
  return String(value ?? "").trim();
}

function isUuidLike(value: string | null | undefined): boolean {
  const normalized = String(value ?? "").trim().toLowerCase();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(normalized);
}

function resolveCrewActivationIdentifiers(group: ComplianceDocumentOwnerGroup): {
  contractorCrewId?: string;
  crewUserId?: string;
} {
  const contractorCrewId = isUuidLike(group.contractorCrewId) ? String(group.contractorCrewId ?? "").trim() : "";
  const crewUserId = String(group.installerUserId ?? "").trim();
  return {
    contractorCrewId: contractorCrewId || undefined,
    crewUserId: crewUserId || undefined,
  };
}

function shortId(value: string | undefined): string {
  return compact(value).slice(-6) || "new";
}

function shortOwnerId(value: string | undefined): string {
  return compact(value).slice(0, 8);
}

function buildContractorQueueSubtitle(contractor: Contractor): string {
  const parts = [
    contractor.entityType ?? "independent",
    compact(contractor.contactName),
    compact(contractor.email),
    compact(contractor.tradeType),
    contractor.parentEntityId ? `child of ${contractor.parentEntityId}` : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

function buildSessionApplicationLabel(
  session: OnboardingSession,
  contractor: Contractor | null,
  token: OnboardingInviteToken | undefined,
) {
  const draft = session.draft;
  const companyName = compact(contractor?.companyName) || compact(draft?.name);
  const contactName = compact(contractor?.contactName) || compact(draft?.contactName);
  const email = compact(contractor?.email) || compact(draft?.contactEmail);
  const phone = compact(contractor?.phone) || compact(draft?.contactPhone);
  const trade = compact(contractor?.tradeType) || compact(draft?.tradeType);
  const regions = contractor?.serviceRegions?.length
    ? contractor.serviceRegions.join(", ")
    : draft?.serviceRegions?.join(", ");
  const zips = contractor?.serviceZipCodes?.length
    ? contractor.serviceZipCodes.join(", ")
    : draft?.serviceZipCodes?.join(", ");
  const serviceArea = compact(contractor?.serviceArea) || compact(regions) || compact(zips);
  const primary = companyName || contactName || email || `Application ${shortId(session.id)}`;
  const subtitle = [
    contactName && contactName !== primary ? contactName : "",
    email,
    phone,
  ].filter(Boolean).join(" · ");
  const metaParts = [
    `Entity: ${session.entityType}`,
    trade ? `Trade: ${trade}` : "",
    serviceArea ? `Service: ${serviceArea}` : "",
    session.parentEntityId ? `Parent: ${session.parentEntityId}` : "",
  ].filter(Boolean);

  return {
    primary,
    subtitle,
    metaParts,
    applicationRef: `Application ${shortId(session.id)}`,
    inviteRef: token ? `Invite ${shortId(token.id)}` : "",
  };
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => compact(item)).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [];
}

function getDraftRecord(session: OnboardingSession): Record<string, unknown> {
  return session.draft as unknown as Record<string, unknown>;
}

function getSessionTradeLabel(session: OnboardingSession, contractor: Contractor | null): string {
  const draft = getDraftRecord(session);
  const tradeValues = toStringArray(draft.tradeType);
  return compact(contractor?.tradeType) || tradeValues.join(", ");
}

function getSessionFootprint(session: OnboardingSession): Record<string, unknown> | undefined {
  const draft = getDraftRecord(session);
  const parent = draft.parent as Record<string, unknown> | undefined;
  return parent?.footprint as Record<string, unknown> | undefined;
}

function getSessionServiceRegions(
  session: OnboardingSession,
  contractor: Contractor | null,
): string[] {
  const draft = getDraftRecord(session);
  const footprint = getSessionFootprint(session);
  return [
    ...(contractor?.serviceRegions ?? []),
    ...toStringArray(draft.serviceRegions),
    ...toStringArray(draft.serviceAreas),
    ...toStringArray(footprint?.serviceAreas),
  ].filter((value, index, all) => value && all.indexOf(value) === index);
}

function getSessionServiceZipCodes(
  session: OnboardingSession,
  contractor: Contractor | null,
): string[] {
  const draft = getDraftRecord(session);
  const footprint = getSessionFootprint(session);
  return [
    ...(contractor?.serviceZipCodes ?? []),
    ...toStringArray(draft.serviceZipCodes),
    ...toStringArray(footprint?.zips).filter((value) => /^\d{5}(?:-\d{4})?$/.test(value)),
  ].filter((value, index, all) => value && all.indexOf(value) === index);
}

function getSessionServiceAreaLabel(
  session: OnboardingSession,
  contractor: Contractor | null,
): string {
  const draft = getDraftRecord(session);
  const footprint = getSessionFootprint(session);
  const regions = getSessionServiceRegions(session, contractor);
  const zips = getSessionServiceZipCodes(session, contractor);
  return (
    compact(contractor?.serviceArea) ||
    compact(draft.serviceAreas) ||
    compact(footprint?.serviceAreas) ||
    regions.join(", ") ||
    zips.join(", ")
  );
}

function parseWeeklyCapacity(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.round(value);
  }
  const text = compact(value);
  if (!text) return null;
  const matches = text.match(/\d+(?:\.\d+)?/g);
  if (!matches?.length) return null;
  const numeric = matches.map(Number).filter((item) => Number.isFinite(item) && item > 0);
  if (!numeric.length) return null;
  return Math.ceil(Math.max(...numeric));
}

function getSessionMaxWeeklyJobs(
  session: OnboardingSession,
  contractor: Contractor | null,
): number | null {
  if (contractor?.maxWeeklyJobs && contractor.maxWeeklyJobs > 0) {
    return contractor.maxWeeklyJobs;
  }
  const draft = getDraftRecord(session);
  const footprint = getSessionFootprint(session);
  return (
    parseWeeklyCapacity(draft.maxWeeklyJobs) ??
    parseWeeklyCapacity(draft.weeklyCapacity) ??
    parseWeeklyCapacity(draft.capacity) ??
    parseWeeklyCapacity(footprint?.capacity)
  );
}

function deriveSpecArchetypesFromOnboarding(
  session: OnboardingSession,
  contractor: Contractor | null,
): SpecArchetype[] {
  const text = [
    getSessionTradeLabel(session, contractor),
    ...toStringArray(getDraftRecord(session).tradeType),
  ].join(" ").toLowerCase();
  const specs = new Set<SpecArchetype>();

  if (text.includes("bath")) specs.add("FULL-BATH");
  if (text.includes("shower")) specs.add("SHWR-RPL");
  if (text.includes("tub-to-shower") || text.includes("tub to shower")) specs.add("TTS-STD");
  if (text.includes("tub") && !specs.has("TTS-STD")) specs.add("TUB-RPL");
  if (text.includes("walk-in") || text.includes("walk in")) specs.add("WALK-IN-TUB");
  if (text.includes("wet")) specs.add("WET-SPACE");
  if (text.includes("window")) specs.add("WINDOW");
  if (text.includes("floor")) specs.add("FLOORING");

  return Array.from(specs);
}

function buildPayProfileFromOnboarding(
  contractorId: string,
  session: OnboardingSession,
  contractor: Contractor | null,
): ContractorPayProfile | null {
  const allowedSpecTypes = deriveSpecArchetypesFromOnboarding(session, contractor);
  if (allowedSpecTypes.length === 0) return null;
  const base = defaultPayProfile(contractorId);
  return {
    ...base,
    id: `PP-${contractorId.slice(0, 8)}`,
    allowedSpecTypes,
    notes: "Created from approved onboarding application.",
  };
}

/**
 * Tone map for canonical session statuses. Labels themselves come from
 * `getCanonicalSessionStatusLabel()` so they stay aligned with
 * `ONBOARDING_LIFECYCLE_LABELS`. Do not duplicate label strings here.
 */
const SESSION_STATUS_TONE: Record<
  Extract<OnboardingSession["status"], "draft" | "submitted" | "reupload_requested" | "approved" | "rejected">,
  string
> = {
  draft: "text-muted-foreground",
  submitted: "text-primary",
  reupload_requested: "text-warning",
  approved: "text-success",
  rejected: "text-destructive",
};


const ALLOWED_ROLES = new Set(["super_admin", "admin", "ops_manager", "it_staff"]);

const BUCKET_META: Record<
  ReviewQueueBucket,
  { label: string; tone: string; icon: typeof AlertTriangle }
> = {
  missing: { label: "Missing", tone: "text-destructive", icon: AlertTriangle },
  expired: { label: "Expired", tone: "text-destructive", icon: AlertTriangle },
  pending_verification: { label: "Pending verification", tone: "text-warning", icon: Clock },
  expiring_soon: { label: "Expiring soon", tone: "text-warning", icon: Clock },
  ready: { label: "Ready", tone: "text-primary", icon: CheckCircle2 },
  suspended: { label: "Suspended", tone: "text-muted-foreground", icon: Ban },
};

const DOC_TYPES: ComplianceDocumentType[] = [
  "license",
  "insurance",
  "w9",
  "agreement",
  "certification",
];

function isWebsiteComplianceDocument(doc: ComplianceDocument): boolean {
  return doc.metadata?.source === "installer_compliance_documents";
}

function getComplianceDocumentStoragePath(doc: ComplianceDocument): string {
  return String(doc.metadata?.storagePath ?? doc.url ?? "").trim();
}

function getComplianceDocumentFileName(doc: ComplianceDocument): string {
  return String(doc.fileName ?? doc.metadata?.fileName ?? doc.metadata?.file_name ?? doc.type).trim();
}

function getComplianceDocumentMimeType(doc: ComplianceDocument): string {
  return String(doc.metadata?.mimeType ?? doc.metadata?.mime_type ?? "").trim().toLowerCase();
}

function getComplianceDocumentPreviewKind(
  doc: ComplianceDocument,
  href: string,
): "image" | "pdf" | "other" {
  const mimeType = getComplianceDocumentMimeType(doc);
  const fileName = getComplianceDocumentFileName(doc).toLowerCase();
  const urlPath = href.split("?")[0]?.toLowerCase() ?? "";
  if (mimeType.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(fileName || urlPath)) {
    return "image";
  }
  if (mimeType === "application/pdf" || /\.pdf$/.test(fileName || urlPath)) {
    return "pdf";
  }
  return "other";
}

function getDocumentStatus(doc: ComplianceDocument): {
  label: string;
  className: string;
} {
  if (doc.rejectedAt) {
    return { label: "Rejected", className: "text-destructive" };
  }
  if (doc.verifiedAt) {
    const expiresAtMs = doc.expiresAt ? Date.parse(doc.expiresAt) : Number.NaN;
    if (Number.isFinite(expiresAtMs) && expiresAtMs < Date.now()) {
      return { label: "Expired", className: "text-destructive" };
    }
    return { label: "Verified", className: "text-success" };
  }
  return { label: "Pending review", className: "text-warning" };
}

type ComplianceDocumentOwnerGroup = {
  key: string;
  label: string;
  subtitle: string;
  ownerKind: "company" | "crew";
  contractorId: string | null;
  installerUserId: string | null;
  contractorCrewId: string | null;
  contractorCrewStatus: string | null;
  docs: ComplianceDocument[];
};

function readDocMetaString(doc: ComplianceDocument, key: string): string {
  const value = doc.metadata?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function getComplianceDocumentOwnerKind(doc: ComplianceDocument): "company" | "crew" {
  const rawType = String(doc.metadata?.rawType ?? doc.type ?? "")
    .trim()
    .toLowerCase();

  if (
    rawType === "background_check" ||
    rawType === "driver_license" ||
    rawType === "osha_10" ||
    rawType === "drug_screen" ||
    rawType === "manufacturer_cert"
  ) {
    return "crew";
  }

  return "company";
}

function groupComplianceDocumentsByOwner(
  docs: ComplianceDocument[],
  contractor?: Contractor | null,
): ComplianceDocumentOwnerGroup[] {
  const groups = new Map<string, ComplianceDocumentOwnerGroup>();

  for (const doc of docs) {
    const ownerKind = getComplianceDocumentOwnerKind(doc);
    const crewName = readDocMetaString(doc, "installerCrewName");
    const installerName = readDocMetaString(doc, "installerFullName");
    const installerEmail = readDocMetaString(doc, "installerEmail");
    const installerUserId = readDocMetaString(doc, "installerUserId");
    const contractorCrewId = readDocMetaString(doc, "contractorCrewId");
    const contractorCrewStatus = readDocMetaString(doc, "contractorCrewStatus");
    const contractorName =
      readDocMetaString(doc, "contractorCompanyName") ||
      contractor?.companyName ||
      "Company";
    const ownerLabel =
      ownerKind === "crew"
        ? crewName ||
          installerName ||
          installerEmail ||
          (installerUserId ? `Crew ${shortOwnerId(installerUserId)}` : "Crew member")
        : contractorName;
    const ownerId =
      ownerKind === "crew"
        ? installerUserId || ownerLabel
        : doc.contractorId || ownerLabel;
    const key = `${ownerKind}:${ownerId}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        label: `${ownerKind === "crew" ? "Crew" : "Company"}: ${ownerLabel}`,
        subtitle:
          ownerKind === "crew"
            ? [
                installerEmail && installerEmail !== ownerLabel ? installerEmail : null,
                installerUserId && !ownerLabel.includes(shortOwnerId(installerUserId))
                  ? `User ${shortOwnerId(installerUserId)}`
                  : null,
                contractorName ? `Contractor: ${contractorName}` : null,
              ].filter(Boolean).join(" • ") || "Crew documents"
            : "Company documents",
        ownerKind,
        contractorId: doc.contractorId || contractor?.id || null,
        installerUserId: installerUserId || null,
        contractorCrewId: contractorCrewId || null,
        contractorCrewStatus: contractorCrewStatus || null,
        docs: [],
      });
    }

    groups.get(key)?.docs.push(doc);
  }

  return Array.from(groups.values()).sort((a, b) => {
    const aCompany = a.key.startsWith("company:");
    const bCompany = b.key.startsWith("company:");
    if (aCompany !== bCompany) return aCompany ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
}

function getCrewActivationKey(group: ComplianceDocumentOwnerGroup): string {
  return group.contractorCrewId || `${group.contractorId ?? "unknown"}:${group.installerUserId ?? group.key}`;
}

function isCrewGroupActive(group: ComplianceDocumentOwnerGroup, activatedCrewKeys: Set<string>): boolean {
  if (group.ownerKind !== "crew") return false;
  return group.contractorCrewStatus === "active" || activatedCrewKeys.has(getCrewActivationKey(group));
}

function canActivateCrewGroup(group: ComplianceDocumentOwnerGroup, activatedCrewKeys: Set<string>): boolean {
  if (group.ownerKind !== "crew" || isCrewGroupActive(group, activatedCrewKeys)) return false;
  if (!group.contractorId || (!group.contractorCrewId && !group.installerUserId)) return false;
  if (group.docs.length === 0) return false;
  return group.docs.every((doc) => !!doc.verifiedAt && !doc.rejectedAt);
}

export default function ComplianceReviewPage() {
  const { contractors, setContractors, setPayProfiles } = useAppData();
  const onboarding = useOnboarding();
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const { toast } = useToast();

  // Document ledger sourced from OnboardingProvider (IN-2.a).
  const ledger = onboarding.complianceDocuments;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [suspendTarget, setSuspendTarget] = useState<Contractor | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<Contractor | null>(null);
  const [reason, setReason] = useState("");
  const [rejectTarget, setRejectTarget] = useState<{
    contractorId: string;
    documentId: string;
  } | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [documentPreview, setDocumentPreview] = useState<{
    doc: ComplianceDocument;
    href: string;
  } | null>(null);

  // Local "add document" form — minimal seed surface (IN-1.d) so the
  // queue isn't empty in a fresh session.
  const [seedType, setSeedType] = useState<ComplianceDocumentType>("insurance");
  const [seedExpires, setSeedExpires] = useState("");

  // IN-2.g — onboarding session admin handoff state.
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [approveTarget, setApproveTarget] = useState<OnboardingSession | null>(null);
  const [rejectSessionTarget, setRejectSessionTarget] = useState<OnboardingSession | null>(null);
  const [reuploadTarget, setReuploadTarget] = useState<OnboardingSession | null>(null);
  const [sessionReason, setSessionReason] = useState("");
  const [reuploadSteps, setReuploadSteps] = useState<string[]>([]);
  const [activatedCrewKeys, setActivatedCrewKeys] = useState<Set<string>>(() => new Set());
  const [manualInviteLink, setManualInviteLink] = useState<string | null>(null);

  const allowed = hasPerm("admin_view");

  const rows = useMemo<ReviewQueueRow[]>(
    () => getComplianceReviewQueue(contractors, ledger),
    [contractors, ledger],
  );

  const grouped = useMemo(() => {
    const out: Record<ReviewQueueBucket, ReviewQueueRow[]> = {
      missing: [],
      expired: [],
      pending_verification: [],
      expiring_soon: [],
      ready: [],
      suspended: [],
    };
    for (const r of rows) out[r.bucket].push(r);
    return out;
  }, [rows]);

  const selected = selectedId ? rows.find((r) => r.contractor.id === selectedId) ?? null : null;
  const selectedDocs = useMemo(
    () =>
      selected
        ? ledger.filter((d) => d.contractorId === selected.contractor.id)
        : [],
    [ledger, selected?.contractor.id],
  );
  const selectedDocGroups = useMemo(
    () => groupComplianceDocumentsByOwner(selectedDocs, selected?.contractor ?? null),
    [selectedDocs, selected?.contractor],
  );

  // IN-2.g — derived session lists.
  const adminSessions = useMemo(() => {
    const visibleStatuses: OnboardingSession["status"][] = [
      "draft",
      "submitted",
      "reupload_requested",
      "approved",
      "rejected",
    ];
    return onboarding.sessions
      .filter((s) => visibleStatuses.includes(s.status))
      .sort((a, b) => (a.lastTouchedAt < b.lastTouchedAt ? 1 : -1));
  }, [onboarding.sessions]);

  useEffect(() => {
    if (adminSessions.length === 0) {
      if (selectedSessionId) setSelectedSessionId(null);
      return;
    }

    const selectedStillVisible = adminSessions.some(
      (session) => session.id === selectedSessionId,
    );
    if (!selectedStillVisible) {
      setSelectedSessionId(adminSessions[0].id);
    }
  }, [adminSessions, selectedSessionId]);

  const contractorById = useMemo(
    () => new Map(contractors.map((contractor) => [contractor.id, contractor])),
    [contractors],
  );

  const tokenById = useMemo(
    () => new Map(onboarding.inviteTokens.map((token) => [token.id, token])),
    [onboarding.inviteTokens],
  );

  const selectedSession = selectedSessionId
    ? onboarding.sessions.find((s) => s.id === selectedSessionId) ?? null
    : null;

  const selectedSessionContractor = selectedSession?.contractorId
    ? contractorById.get(selectedSession.contractorId) ?? null
    : null;

  const selectedSessionToken = selectedSession
    ? tokenById.get(selectedSession.tokenId)
    : undefined;

  const selectedSessionLabel = selectedSession
    ? buildSessionApplicationLabel(
        selectedSession,
        selectedSessionContractor,
        selectedSessionToken,
      )
    : null;

  const selectedSessionReadiness = useMemo(
    () =>
      selectedSession
        ? onboarding.getAdminApprovalReadiness(selectedSession.id)
        : null,
    [selectedSession, onboarding],
  );

  const selectedSessionDocSummary = useMemo(
    () =>
      selectedSession
        ? getOnboardingSessionDocSummary(selectedSession, ledger)
        : null,
    [selectedSession, ledger],
  );

  const selectedSessionDocs = useMemo(
    () =>
      selectedSession
        ? getOnboardingDocumentsForSession(ledger, selectedSession)
        : [],
    [selectedSession, ledger],
  );

  const selectedSessionDocGroups = useMemo(
    () =>
      groupComplianceDocumentsByOwner(
        selectedSessionDocs,
        selectedSessionContractor,
      ),
    [selectedSessionDocs, selectedSessionContractor],
  );





  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <Card>
          <CardContent className="p-6 space-y-2">
            <Shield className="h-8 w-8 text-warning mx-auto" />
            <p className="text-sm font-semibold">Restricted</p>
            <p className="text-xs text-muted-foreground">
              Compliance Review is available to SuperAdmin / Admin / Ops Manager only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const actor = `${role}-user`;

  async function handleVerify(documentId: string) {
    try {
      const doc = ledger.find((d) => d.id === documentId);
      if (doc && isWebsiteComplianceDocument(doc)) {
        await approveInstallerComplianceDocument(documentId);
      }
      onboarding.verifyComplianceDocument(documentId, actor);
      toast({ title: "Document verified", description: "Compliance status refreshed." });
    } catch (e) {
      toast({
        title: "Couldn't verify document",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  async function handleRejectConfirm() {
    if (!rejectTarget) return;
    if (rejectReason.trim().length < 5) {
      toast({
        title: "Reason too short",
        description: "Please provide a brief rejection reason (≥5 chars).",
        variant: "destructive",
      });
      return;
    }
    try {
      const doc = ledger.find((d) => d.id === rejectTarget.documentId);
      if (doc && isWebsiteComplianceDocument(doc)) {
        await rejectInstallerComplianceDocument(rejectTarget.documentId, rejectReason.trim());
      }
      onboarding.rejectComplianceDocument(
        rejectTarget.documentId,
        actor,
        rejectReason.trim(),
      );
      setRejectTarget(null);
      setRejectReason("");
      toast({ title: "Document rejected" });
    } catch (e) {
      toast({
        title: "Couldn't reject document",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  async function handleViewDocument(doc: ComplianceDocument) {
    try {
      const storagePath = getComplianceDocumentStoragePath(doc);
      if (!storagePath) {
        throw new Error("This document is missing its storage path.");
      }
      const href = isWebsiteComplianceDocument(doc)
        ? await getInstallerComplianceDocumentReadUrl(doc.id, 300)
        : storagePath;
      if (!href) throw new Error("Document URL was not returned.");
      setDocumentPreview({ doc, href });
    } catch (e) {
      toast({
        title: "Couldn't open document",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  async function handleActivateCrew(group: ComplianceDocumentOwnerGroup) {
    try {
      if (group.ownerKind !== "crew") {
        throw new Error("Only crew document groups can be activated here.");
      }
      const contractorId = String(group.contractorId ?? group.docs[0]?.contractorId ?? "").trim();
      if (!contractorId) {
        throw new Error("Crew group is missing contractor linkage.");
      }
      const contractor = contractorById.get(contractorId) ?? null;
      const organizationId = String(
        contractor?.organizationId ?? group.docs[0]?.metadata?.organizationId ?? "",
      ).trim();
      if (!organizationId) {
        throw new Error("Crew group is missing organization linkage.");
      }
      if (!group.contractorCrewId && !group.installerUserId) {
        throw new Error("Crew group is missing crew identity.");
      }
      if (!canActivateCrewGroup(group, activatedCrewKeys)) {
        throw new Error("Verify all crew documents before activating this crew member.");
      }
      const identifiers = resolveCrewActivationIdentifiers(group);
      if (!identifiers.contractorCrewId && !identifiers.crewUserId) {
        throw new Error("Crew group is missing a valid activation identifier.");
      }

      await activateInstallerComplianceCrew({
        organizationId,
        contractorId,
        contractorCrewId: identifiers.contractorCrewId,
        crewUserId: identifiers.crewUserId,
      });

      setActivatedCrewKeys((prev) => {
        const next = new Set(prev);
        next.add(getCrewActivationKey(group));
        return next;
      });
      toast({
        title: "Crew activated",
        description: `${group.label.replace(/^Crew:\s*/i, "")} can now access the active crew workflow.`,
      });
    } catch (e) {
      toast({
        title: "Couldn't activate crew",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  function handleSuspendConfirm() {
    if (!suspendTarget) return;
    try {
      const result = suspendContractor(suspendTarget, reason, actor);
      if (result.changed) {
        setContractors((prev) =>
          prev.map((c) => (c.id === result.contractor.id ? result.contractor : c)),
        );
        toast({ title: "Contractor suspended" });
      } else {
        toast({ title: "Already suspended" });
      }
      setSuspendTarget(null);
      setReason("");
    } catch (e) {
      toast({
        title: "Couldn't suspend",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  function handleReactivateConfirm() {
    if (!reactivateTarget) return;
    try {
      const result = reactivateContractor(reactivateTarget, reason, actor);
      if (result.changed) {
        setContractors((prev) =>
          prev.map((c) => (c.id === result.contractor.id ? result.contractor : c)),
        );
        toast({ title: "Contractor reactivated" });
      } else {
        toast({ title: "Not currently suspended" });
      }
      setReactivateTarget(null);
      setReason("");
    } catch (e) {
      toast({
        title: "Couldn't reactivate",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  function handleSeedDocument() {
    if (!selected) return;
    try {
      onboarding.submitComplianceDocument(
        {
          contractorId: selected.contractor.id,
          type: seedType,
          expiresAt: seedExpires ? new Date(seedExpires).toISOString() : undefined,
        },
        actor,
      );
      setSeedExpires("");
      toast({ title: "Document submitted", description: "Awaiting verification." });
    } catch (e) {
      toast({
        title: "Couldn't submit document",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  // ── IN-2.g — Onboarding session admin handoff handlers ──────────
  async function handleApproveSession() {
    if (!approveTarget) return;
    if (sessionReason.trim().length < MIN_HANDOFF_REASON) return;
    try {
      const result = onboarding.approveOnboarding(
        approveTarget.id,
        actor,
        sessionReason.trim(),
      );
      if (result.contractorPatch) {
        const { contractorId, vettingStatus } = result.contractorPatch;
        const currentContractor = findSessionContractor(approveTarget, contractors);
        const serviceRegions = getSessionServiceRegions(approveTarget, currentContractor);
        const serviceZipCodes = getSessionServiceZipCodes(approveTarget, currentContractor);
        const serviceArea = getSessionServiceAreaLabel(approveTarget, currentContractor);
        const maxWeeklyJobs = getSessionMaxWeeklyJobs(approveTarget, currentContractor);
        const tradeType = getSessionTradeLabel(approveTarget, currentContractor);
        const contractorUpdates: Partial<Contractor> = {
          active: true,
          availabilityStatus: "Available",
          marketplaceOptIn: true,
          ...(serviceArea ? { serviceArea } : {}),
          ...(serviceRegions.length > 0 ? { serviceRegions } : {}),
          ...(serviceZipCodes.length > 0 ? { serviceZipCodes } : {}),
          ...(maxWeeklyJobs ? { maxWeeklyJobs } : {}),
          ...(tradeType ? { tradeType } : {}),
        };
        const persistedContractor = await ContractorService.updateContractor(
          contractorId,
          contractorUpdates,
        );
        const bridgedContractor: Partial<Contractor> = persistedContractor ?? {
          ...(currentContractor ?? {}),
          ...contractorUpdates,
          id: contractorId,
        };
        setContractors((prev) =>
          prev.map((c) =>
            c.id === contractorId
              ? {
                  ...c,
                  ...bridgedContractor,
                  vettingStatus,
                  onboardingStatus: "Approved",
                }
              : c,
          ),
        );
        const onboardingPayProfile = buildPayProfileFromOnboarding(
          contractorId,
          approveTarget,
          currentContractor,
        );
        if (onboardingPayProfile) {
          setPayProfiles((prev) => {
            const existing = prev.find((profile) => profile.contractorId === contractorId);
            if (!existing) return [...prev, onboardingPayProfile];
            const mergedSpecTypes = Array.from(
              new Set([
                ...existing.allowedSpecTypes,
                ...onboardingPayProfile.allowedSpecTypes,
              ]),
            );
            return prev.map((profile) =>
              profile.contractorId === contractorId
                ? {
                    ...profile,
                    isActive: true,
                    allowedSpecTypes: mergedSpecTypes,
                    updatedAt: new Date().toISOString(),
                  }
                : profile,
            );
          });
        }
      }
      setApproveTarget(null);
      setSessionReason("");
      toast({ title: "Onboarding approved" });
    } catch (e) {
      toast({
        title: "Couldn't approve",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  async function handleSendInviteSession(session: OnboardingSession) {
    try {
      const identity = resolveInviteIdentity(session);
      if (!identity?.email) {
        throw new Error("Applicant email is missing on this application.");
      }
      const contractor = findSessionContractor(session, contractors);
      const organizationId = String(contractor?.organizationId ?? "").trim();
      if (!session.contractorId || !organizationId) {
        throw new Error("Application is missing contractor/org linkage. Recreate the onboarding session or repair the contractor organization first.");
      }
      const redirectTo = getInstallerPortalInviteRedirect();
      const { data: inviteData, error: inviteError } = await supabase.functions.invoke<{
        delivery?: string;
        actionLink?: string | null;
      }>("create-auth-user", {
        body: {
          email: identity.email,
          fullName: identity.fullName,
          organizationId,
          contractorId: session.contractorId,
          orgRole: "owner",
          sendInvite: true,
          redirectTo,
        },
      });
      if (inviteError) throw inviteError;
      if (inviteData?.delivery === "manual_link" && inviteData.actionLink) {
        await navigator.clipboard?.writeText(inviteData.actionLink).catch(() => undefined);
        setManualInviteLink(inviteData.actionLink);
        toast({
          title: "Invite link generated",
          description: "Supabase could not send the email, so a manual portal invite link is ready.",
        });
        return;
      }
      toast({
        title: "Invite sent",
        description: `Portal invite sent to ${identity.email}.`,
      });
    } catch (e) {
      toast({
        title: "Couldn't send invite",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  async function handleSendInviteContractor(contractor: Contractor) {
    try {
      const email = String(contractor.email ?? "").trim().toLowerCase();
      if (!email) {
        throw new Error("Contractor email is missing.");
      }
      const organizationId = String(contractor.organizationId ?? "").trim();
      if (!organizationId) {
        throw new Error("Contractor organization is missing. Repair the contractor org before sending the portal invite.");
      }
      const fullName = String(contractor.contactName ?? contractor.companyName ?? "Installer Applicant").trim();
      const redirectTo = getInstallerPortalInviteRedirect();
      const { data: inviteData, error: inviteError } = await supabase.functions.invoke<{
        delivery?: string;
        actionLink?: string | null;
      }>("create-auth-user", {
        body: {
          email,
          fullName,
          organizationId,
          contractorId: contractor.id,
          orgRole: "owner",
          sendInvite: true,
          redirectTo,
        },
      });
      if (inviteError) throw inviteError;
      if (inviteData?.delivery === "manual_link" && inviteData.actionLink) {
        await navigator.clipboard?.writeText(inviteData.actionLink).catch(() => undefined);
        setManualInviteLink(inviteData.actionLink);
        toast({
          title: "Invite link generated",
          description: "Supabase could not send the email, so a manual portal invite link is ready.",
        });
        return;
      }
      toast({
        title: "Invite sent",
        description: `Portal invite sent to ${email}.`,
      });
    } catch (e) {
      toast({
        title: "Couldn't send invite",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  function handleVerifyPayout(session: OnboardingSession) {
    try {
      if (!session.payoutMethod) {
        throw new Error("No payout method is recorded for this application.");
      }
      onboarding.setOnboardingPayout(
        session.id,
        {
          type: session.payoutMethod.type,
          last4: session.payoutMethod.last4,
          parentEntityId: session.payoutMethod.parentEntityId,
          placeholderVerified: true,
        },
        actor,
      );
      toast({
        title: "Payout verified",
        description: "Payout verification warning cleared for this application.",
      });
    } catch (e) {
      toast({
        title: "Couldn't verify payout",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  function handleRejectSession() {
    if (!rejectSessionTarget) return;
    if (sessionReason.trim().length < MIN_HANDOFF_REASON) return;
    try {
      onboarding.rejectOnboarding(
        rejectSessionTarget.id,
        actor,
        sessionReason.trim(),
      );
      setRejectSessionTarget(null);
      setSessionReason("");
      toast({ title: "Onboarding rejected" });
    } catch (e) {
      toast({
        title: "Couldn't reject",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }

  function handleRequestReupload() {
    if (!reuploadTarget) return;
    if (sessionReason.trim().length < MIN_HANDOFF_REASON) return;
    if (reuploadSteps.length === 0) return;
    try {
      onboarding.requestOnboardingReupload(
        reuploadTarget.id,
        reuploadSteps,
        sessionReason.trim(),
        actor,
      );
      setReuploadTarget(null);
      setReuploadSteps([]);
      setSessionReason("");
      toast({ title: "Reupload Requested" });
    } catch (e) {
      toast({
        title: "Couldn't request reupload",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  }
  return (
    <CockpitPage
      title="Compliance Review"
      subtitle="IN-1.d — soft-enforcement advisory queue. No dispatch block."
    >
      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        {/* ── Queue ───────────────────────────────────────────────────── */}
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {REVIEW_QUEUE_BUCKET_ORDER.map((bucket) => {
              const items = grouped[bucket];
              const meta = BUCKET_META[bucket];
              const Icon = meta.icon;
              return (
                <div key={bucket} className="border-b last:border-b-0">
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 text-xs font-semibold">
                    <Icon className={`h-3.5 w-3.5 ${meta.tone}`} />
                    <span className={meta.tone}>{meta.label}</span>
                    <Badge variant="outline" className="ml-auto">
                      {items.length}
                    </Badge>
                  </div>
                  {items.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-muted-foreground">None.</p>
                  ) : (
                    items.map((r) => (
                      <button
                        key={r.contractor.id}
                        onClick={() => setSelectedId(r.contractor.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-accent/50 transition ${
                          selectedId === r.contractor.id
                            ? "bg-primary/10 ring-1 ring-primary/30 text-primary"
                            : ""
                        }`}
                      >
                        <div className="font-medium truncate">{r.contractor.companyName}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {buildContractorQueueSubtitle(r.contractor)}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* ── Inspector ──────────────────────────────────────────────── */}
        <Card>
          <CardContent className="p-4 space-y-4">
            {!selected ? (
              <p className="text-sm text-muted-foreground">
                Select a contractor to review their compliance state.
              </p>
            ) : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold">{selected.contractor.companyName}</h2>
                    <p className="text-xs text-muted-foreground">
                      {selected.contractor.entityType ?? "independent"} ·{" "}
                      vettingStatus: {selected.contractor.vettingStatus ?? "trial"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleSendInviteContractor(selected.contractor)}
                    >
                      Send Invite
                    </Button>
                    {selected.contractor.vettingStatus === "blocked" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setReactivateTarget(selected.contractor);
                          setReason("");
                        }}
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivate
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSuspendTarget(selected.contractor);
                          setReason("");
                        }}
                      >
                        <Ban className="h-3.5 w-3.5 mr-1" /> Suspend
                      </Button>
                    )}
                  </div>
                </div>

                {/* Requirements */}
                {selected.status && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase">
                      Requirements
                    </p>
                    <div className="space-y-1">
                      {selected.status.requirements.map((r) => (
                        <div
                          key={r.requirement}
                          className="flex items-center justify-between text-xs border rounded px-2 py-1"
                        >
                          <span className="font-mono">{r.requirement}</span>
                          <Badge
                            variant="outline"
                            className={
                              r.status === "ok" || r.status === "not_required"
                                ? "text-primary"
                                : r.status === "expired" ||
                                    r.status === "missing" ||
                                    r.status === "rejected"
                                  ? "text-destructive"
                                  : "text-warning"
                            }
                          >
                            {r.status}
                            {r.inheritedFromParent ? " · inherited" : ""}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase">
                    Documents
                  </p>
                  {selectedDocs.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No documents on file.</p>
                  ) : (
                    selectedDocGroups.map((group) => (
                      <div key={group.key} className="space-y-1.5 rounded border p-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{group.label}</p>
                            <p className="truncate text-[11px] text-muted-foreground">
                              {group.subtitle}
                            </p>
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            {group.ownerKind === "crew" && (
                              <>
                                <Badge
                                  variant="outline"
                                  className={
                                    isCrewGroupActive(group, activatedCrewKeys)
                                      ? "text-[10px] text-success"
                                      : "text-[10px] text-warning"
                                  }
                                >
                                  {isCrewGroupActive(group, activatedCrewKeys)
                                    ? "active"
                                    : group.contractorCrewStatus || "not active"}
                                </Badge>
                                {!isCrewGroupActive(group, activatedCrewKeys) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={!canActivateCrewGroup(group, activatedCrewKeys)}
                                    onClick={() => void handleActivateCrew(group)}
                                  >
                                    Activate crew
                                  </Button>
                                )}
                              </>
                            )}
                            <Badge variant="outline" className="text-[10px]">
                              {group.docs.length}
                            </Badge>
                          </div>
                        </div>
                        <div className="space-y-1">
                          {group.docs.map((d) => (
                            <div
                              key={d.id}
                              className="flex items-center justify-between gap-2 rounded border bg-background/60 px-2 py-1.5 text-xs"
                            >
                              <div className="min-w-0">
                                <div className="font-mono truncate">{d.type}</div>
                                <div className="text-muted-foreground truncate">
                                  {d.verifiedAt
                                    ? `verified ${new Date(d.verifiedAt).toLocaleDateString()}`
                                    : d.rejectedAt
                                      ? `rejected: ${d.rejectionReason}`
                                      : "pending verification"}
                                  {d.expiresAt ? ` · exp ${new Date(d.expiresAt).toLocaleDateString()}` : ""}
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {getComplianceDocumentStoragePath(d) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => void handleViewDocument(d)}
                                  >
                                    View
                                  </Button>
                                )}
                                {!d.verifiedAt && !d.rejectedAt && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => void handleVerify(d.id)}
                                    >
                                      Verify
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setRejectTarget({
                                          contractorId: selected.contractor.id,
                                          documentId: d.id,
                                        });
                                        setRejectReason("");
                                      }}
                                    >
                                      Reject
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Seed form */}
                <div className="border-t pt-3 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase">
                    Submit document (admin)
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <select
                        className="border rounded px-2 py-1 text-xs bg-background"
                        value={seedType}
                        onChange={(e) => setSeedType(e.target.value as ComplianceDocumentType)}
                      >
                        {DOC_TYPES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Expires (optional)</Label>
                      <Input
                        type="date"
                        value={seedExpires}
                        onChange={(e) => setSeedExpires(e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                    <Button size="sm" onClick={handleSeedDocument}>
                      Submit
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── IN-2.g — Onboarding Sessions panel ─────────────────────── */}
      <div className="mt-6" data-testid="onboarding-sessions-panel">
        <div
          className="mb-3 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 flex items-center gap-2 flex-wrap text-xs"
          data-testid="lifecycle-ownership-affordance"
        >
          <Shield className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="text-foreground font-medium">
            Lifecycle owned by Installer Onboarding Cockpit.
          </span>
          <span className="text-muted-foreground">
            This workspace performs document operations + handoff actions; all
            lifecycle transitions route through the canonical writer.
          </span>
          <a
            href="/contractor-onboarding"
            className="ml-auto text-primary hover:underline inline-flex items-center gap-1"
            data-testid="lifecycle-ownership-link"
          >
            Open cockpit →
          </a>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Inbox className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold">Onboarding Sessions</h2>
          <Badge variant="outline" className="ml-1">{adminSessions.length}</Badge>
          <span className="ml-auto text-xs text-muted-foreground">
            IN-2.g — manual admin handoff. No auto-approval, no payout execution, no portal unlock.
          </span>
        </div>
        <div className="grid gap-4 lg:grid-cols-[1.2fr_1.6fr]">
          <Card className="overflow-hidden">
            <CardContent className="p-0">
              {adminSessions.length === 0 ? (
                <div
                  className="px-4 py-10 flex flex-col items-center text-center"
                  data-testid="compliance-review-empty"
                >
                  <div className="h-11 w-11 rounded-full bg-muted grid place-items-center mb-3">
                    <Inbox className="h-5 w-5 text-muted-foreground/70" />
                  </div>
                  <p className="text-sm font-medium text-foreground">
                    Nothing awaiting compliance review
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 max-w-[14rem]">
                    Submitted onboarding packets will appear here for verification.
                  </p>
                </div>
              ) : (
                adminSessions.map((s) => {
                  const tone = SESSION_STATUS_TONE[
                    s.status as keyof typeof SESSION_STATUS_TONE
                  ];
                  const statusLabel = getCanonicalSessionStatusLabel(s, "admin");
                  const isActive = selectedSessionId === s.id;
                  const contractor = s.contractorId
                    ? contractorById.get(s.contractorId) ?? null
                    : null;
                  const token = tokenById.get(s.tokenId);
                  const label = buildSessionApplicationLabel(s, contractor, token);
                  return (
                    <button
                      key={s.id}
                      data-testid={`onboarding-session-row-${s.id}`}
                      onClick={() => setSelectedSessionId(s.id)}
                      className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-accent/50 transition ${
                        isActive ? "bg-primary/10 ring-1 ring-primary/30" : ""
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="text-sm font-medium truncate">{label.primary}</div>
                            <Badge variant="secondary" className="text-[10px] shrink-0">
                              {label.applicationRef}
                            </Badge>
                          </div>
                          {label.subtitle && (
                            <div className="text-xs text-muted-foreground truncate">
                              {label.subtitle}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground truncate">
                            {label.metaParts.join(" · ")}
                          </div>
                        </div>
                        <Badge variant="outline" className={tone}>
                          {statusLabel}
                        </Badge>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Updated {new Date(s.lastTouchedAt).toLocaleString()}
                      </div>
                    </button>
                  );
                })

              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-3">
              {!selectedSession ? (
                <p className="text-sm text-muted-foreground" data-testid="onboarding-session-empty">
                  Select a session to inspect documents and readiness.
                </p>
              ) : (
                <div data-testid="onboarding-session-inspector" className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold">
                          {selectedSessionLabel?.primary}
                        </p>
                        {selectedSessionLabel?.applicationRef && (
                          <Badge variant="secondary" className="text-[10px]">
                            {selectedSessionLabel.applicationRef}
                          </Badge>
                        )}
                        {selectedSessionLabel?.inviteRef && (
                          <Badge variant="outline" className="text-[10px]">
                            {selectedSessionLabel.inviteRef}
                          </Badge>
                        )}
                      </div>
                      {selectedSessionLabel?.subtitle && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {selectedSessionLabel.subtitle}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {selectedSessionLabel?.metaParts.join(" · ")}
                        {" · "}
                        {getCanonicalSessionStatusLabel(selectedSession, "admin")}
                      </p>
                      {selectedSessionContractor && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Contractor: {selectedSessionContractor.companyName} · {selectedSessionContractor.id}
                        </p>
                      )}
                    </div>
                    <Badge
                      variant="outline"
                      className={
                        SESSION_STATUS_TONE[
                          selectedSession.status as keyof typeof SESSION_STATUS_TONE
                        ]
                      }
                    >
                      {getCanonicalSessionStatusLabel(selectedSession, "admin")}
                    </Badge>
                  </div>


                  {selectedSessionDocSummary && (
                    <div
                      data-testid="onboarding-session-doc-summary"
                      className="grid grid-cols-4 gap-2 text-xs"
                    >
                      <div className="border rounded p-2 text-center">
                        <p className="text-[10px] text-muted-foreground">Total</p>
                        <p className="font-semibold">{selectedSessionDocSummary.total}</p>
                      </div>
                      <div className="border rounded p-2 text-center text-success">
                        <p className="text-[10px] text-muted-foreground">Verified</p>
                        <p className="font-semibold">{selectedSessionDocSummary.verified}</p>
                      </div>
                      <div className="border rounded p-2 text-center text-warning">
                        <p className="text-[10px] text-muted-foreground">Pending</p>
                        <p className="font-semibold">{selectedSessionDocSummary.pending}</p>
                      </div>
                      <div className="border rounded p-2 text-center text-destructive">
                        <p className="text-[10px] text-muted-foreground">Rejected</p>
                        <p className="font-semibold">{selectedSessionDocSummary.rejected}</p>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2" data-testid="onboarding-session-documents">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase">
                        Uploaded documents
                      </p>
                      <Badge variant="outline" className="text-[10px]">
                        {selectedSessionDocs.length}
                      </Badge>
                    </div>
                    {selectedSessionDocs.length === 0 ? (
                      <p className="rounded border border-dashed px-3 py-4 text-xs text-muted-foreground">
                        No uploaded documents are linked to this application yet.
                      </p>
                    ) : (
                      selectedSessionDocGroups.map((group) => (
                        <div key={group.key} className="space-y-1.5 rounded border p-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0">
                              <p className="truncate text-sm font-semibold">{group.label}</p>
                              <p className="truncate text-[11px] text-muted-foreground">
                                {group.subtitle}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              {group.ownerKind === "crew" && (
                                <>
                                  <Badge
                                    variant="outline"
                                    className={
                                      isCrewGroupActive(group, activatedCrewKeys)
                                        ? "text-[10px] text-success"
                                        : "text-[10px] text-warning"
                                    }
                                  >
                                    {isCrewGroupActive(group, activatedCrewKeys)
                                      ? "active"
                                      : group.contractorCrewStatus || "not active"}
                                  </Badge>
                                  {!isCrewGroupActive(group, activatedCrewKeys) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={!canActivateCrewGroup(group, activatedCrewKeys)}
                                      onClick={() => void handleActivateCrew(group)}
                                    >
                                      Activate crew
                                    </Button>
                                  )}
                                </>
                              )}
                              <Badge variant="outline" className="text-[10px]">
                                {group.docs.length}
                              </Badge>
                            </div>
                          </div>
                          <div className="space-y-1">
                            {group.docs.map((doc) => {
                              const status = getDocumentStatus(doc);
                              return (
                                <div
                                  key={doc.id}
                                  className="rounded border bg-background/60 px-3 py-2 text-xs"
                                  data-testid={`onboarding-session-document-${doc.id}`}
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-foreground">{doc.type}</span>
                                        <Badge variant="outline" className={status.className}>
                                          {status.label}
                                        </Badge>
                                        {doc.metadata?.rawType && doc.metadata.rawType !== doc.type && (
                                          <Badge variant="secondary" className="text-[10px]">
                                            {String(doc.metadata.rawType)}
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="mt-1 truncate text-muted-foreground">
                                        {doc.fileName || "Uploaded document"}
                                        {" · "}
                                        Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                                        {doc.expiresAt
                                          ? ` · Expires ${new Date(doc.expiresAt).toLocaleDateString()}`
                                          : ""}
                                      </p>
                                      {doc.rejectionReason && (
                                        <p className="mt-1 text-destructive">
                                          Rejection: {doc.rejectionReason}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                      {getComplianceDocumentStoragePath(doc) && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => void handleViewDocument(doc)}
                                        >
                                          View
                                        </Button>
                                      )}
                                      {!doc.verifiedAt && !doc.rejectedAt && (
                                        <>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => void handleVerify(doc.id)}
                                          >
                                            Verify
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                              setRejectTarget({
                                                contractorId: doc.contractorId,
                                                documentId: doc.id,
                                              });
                                              setRejectReason("");
                                            }}
                                          >
                                            Reject
                                          </Button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {selectedSessionReadiness && (
                    <div className="space-y-1" data-testid="onboarding-session-readiness">
                      {selectedSessionReadiness.blockers.length > 0 && (
                        <div className="border border-destructive/30 bg-destructive/5 rounded p-2 space-y-0.5">
                          <p className="text-xs font-semibold text-destructive flex items-center gap-1">
                            <FileWarning className="h-3 w-3" /> Blockers
                          </p>
                          {selectedSessionReadiness.blockers.map((b) => (
                            <p
                              key={b}
                              className="text-xs text-destructive"
                              data-testid="onboarding-session-blocker"
                            >
                              • {b}
                            </p>
                          ))}
                        </div>
                      )}
                      {selectedSessionReadiness.warnings.length > 0 && (
                        <div className="border border-warning/30 bg-warning/5 rounded p-2 space-y-0.5">
                          <p className="text-xs font-semibold text-warning">Warnings</p>
                          {selectedSessionReadiness.warnings.map((w) => (
                            <p key={w} className="text-xs text-warning">
                              • {w}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {(selectedSession.status === "submitted" ||
                    selectedSession.status === "reupload_requested" ||
                    selectedSession.status === "approved") && (
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="onboarding-send-invite-button"
                        onClick={() => void handleSendInviteSession(selectedSession)}
                      >
                        Send Invite
                      </Button>
                      {selectedSession.payoutMethod && !selectedSession.payoutMethod.verified && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid="onboarding-verify-payout-button"
                          onClick={() => handleVerifyPayout(selectedSession)}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Verify payout
                        </Button>
                      )}
                      {selectedSession.status === "submitted" && (
                        <Button
                          size="sm"
                          data-testid="onboarding-approve-button"
                          disabled={!selectedSessionReadiness?.ready}
                          onClick={() => {
                            setApproveTarget(selectedSession);
                            setSessionReason("");
                          }}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                        </Button>
                      )}
                      {(selectedSession.status === "submitted" ||
                        selectedSession.status === "reupload_requested") && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid="onboarding-reupload-button"
                            onClick={() => {
                              setReuploadTarget(selectedSession);
                              setReuploadSteps([]);
                              setSessionReason("");
                            }}
                          >
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Request reupload
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            data-testid="onboarding-reject-button"
                            onClick={() => {
                              setRejectSessionTarget(selectedSession);
                              setSessionReason("");
                            }}
                          >
                            <Ban className="h-3.5 w-3.5 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* IN-2.g Approve dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve onboarding</DialogTitle>
            <DialogDescription>
              Marks the session approved. Reason ≥ {MIN_HANDOFF_REASON} chars required.
              No payout is executed and no dispatch hard-block changes.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="onboarding-approve-reason"
            placeholder="Approval rationale (min 10 chars)"
            value={sessionReason}
            onChange={(e) => setSessionReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)}>Cancel</Button>
            <Button
              data-testid="onboarding-approve-confirm"
              onClick={handleApproveSession}
              disabled={sessionReason.trim().length < MIN_HANDOFF_REASON}
            >
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IN-2.g Reject dialog */}
      <Dialog open={!!rejectSessionTarget} onOpenChange={(o) => !o && setRejectSessionTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject onboarding</DialogTitle>
            <DialogDescription>
              Terminal. Cannot be unset. Reason ≥ {MIN_HANDOFF_REASON} chars required.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="onboarding-reject-reason"
            placeholder="Rejection rationale (min 10 chars)"
            value={sessionReason}
            onChange={(e) => setSessionReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectSessionTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              data-testid="onboarding-reject-confirm"
              onClick={handleRejectSession}
              disabled={sessionReason.trim().length < MIN_HANDOFF_REASON}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IN-2.g Reupload dialog */}
      <Dialog open={!!reuploadTarget} onOpenChange={(o) => !o && setReuploadTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request reupload</DialogTitle>
            <DialogDescription>
              Sends the session back to draft. Select at least one step. Reason ≥ {MIN_HANDOFF_REASON} chars.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Steps to redo</Label>
            <div
              className="flex flex-wrap gap-2"
              data-testid="onboarding-reupload-step-picker"
            >
              {reuploadTarget &&
                getOnboardingSteps(reuploadTarget).map((step) => {
                  const checked = reuploadSteps.includes(step.key);
                  return (
                    <button
                      key={step.key}
                      type="button"
                      data-testid={`onboarding-reupload-step-${step.key}`}
                      onClick={() =>
                        setReuploadSteps((prev) =>
                          prev.includes(step.key)
                            ? prev.filter((k) => k !== step.key)
                            : [...prev, step.key],
                        )
                      }
                      className={`px-2 py-1 rounded border text-xs transition ${
                        checked
                          ? "bg-primary/15 text-primary ring-1 ring-primary/30"
                          : "border-border text-muted-foreground hover:bg-accent/40"
                      }`}
                    >
                      {step.title ?? step.key}
                    </button>
                  );
                })}
            </div>
            <Textarea
              data-testid="onboarding-reupload-reason-input"
              placeholder="Reupload reason (min 10 chars)"
              value={sessionReason}
              onChange={(e) => setSessionReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReuploadTarget(null)}>Cancel</Button>
            <Button
              data-testid="onboarding-reupload-confirm"
              onClick={handleRequestReupload}
              disabled={
                sessionReason.trim().length < MIN_HANDOFF_REASON ||
                reuploadSteps.length === 0
              }
            >
              Request reupload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Suspend dialog */}
      <Dialog open={!!suspendTarget} onOpenChange={(o) => !o && setSuspendTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend contractor</DialogTitle>
            <DialogDescription>
              Sets vettingStatus to "blocked". Reason ≥ {MIN_SUSPENSION_REASON_LENGTH} chars
              required. Append-only audit entry recorded.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for suspension (min 10 chars)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSuspendConfirm}
              disabled={reason.trim().length < MIN_SUSPENSION_REASON_LENGTH}
            >
              Suspend
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate dialog */}
      <Dialog open={!!reactivateTarget} onOpenChange={(o) => !o && setReactivateTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reactivate contractor</DialogTitle>
            <DialogDescription>
              Restores prior vettingStatus. Reason ≥ {MIN_SUSPENSION_REASON_LENGTH} chars required.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason for reactivation (min 10 chars)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleReactivateConfirm}
              disabled={reason.trim().length < MIN_SUSPENSION_REASON_LENGTH}
            >
              Reactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document preview dialog */}
      <Dialog open={!!documentPreview} onOpenChange={(o) => !o && setDocumentPreview(null)}>
        <DialogContent className="max-h-[92vh] max-w-6xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {documentPreview
                ? getComplianceDocumentFileName(documentPreview.doc)
                : "Compliance document"}
            </DialogTitle>
            <DialogDescription>
              Previewing the compliance file in this review workspace.
            </DialogDescription>
          </DialogHeader>
          {documentPreview && (() => {
            const title = getComplianceDocumentFileName(documentPreview.doc);
            const kind = getComplianceDocumentPreviewKind(documentPreview.doc, documentPreview.href);

            if (kind === "image") {
              return (
                <div className="max-h-[72vh] overflow-auto rounded-md border bg-background">
                  <img
                    src={documentPreview.href}
                    alt={title}
                    className="mx-auto max-h-[72vh] w-full object-contain"
                  />
                </div>
              );
            }

            if (kind === "pdf") {
              return (
                <iframe
                  src={documentPreview.href}
                  title={title}
                  className="h-[72vh] w-full rounded-md border bg-background"
                />
              );
            }

            return (
              <div className="space-y-3">
                <div className="rounded-md border bg-muted/20 p-3 text-sm text-muted-foreground">
                  This file type may not render in every browser. If the preview stays blank,
                  use the browser download control inside the embedded viewer.
                </div>
                <iframe
                  src={documentPreview.href}
                  title={title}
                  className="h-[64vh] w-full rounded-md border bg-background"
                />
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentPreview(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!manualInviteLink} onOpenChange={(o) => !o && setManualInviteLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual invite link</DialogTitle>
            <DialogDescription>
              Supabase could not send the invite email. Send this link to the installer so they can activate their portal access.
            </DialogDescription>
          </DialogHeader>
          <Input value={manualInviteLink ?? ""} readOnly className="font-mono text-xs" />
          <DialogFooter>
            <Button variant="outline" onClick={() => manualInviteLink && window.open(manualInviteLink, "_blank", "noopener,noreferrer")}>
              Open link
            </Button>
            <Button onClick={() => manualInviteLink && navigator.clipboard?.writeText(manualInviteLink)}>
              Copy link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject document dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject document</DialogTitle>
            <DialogDescription>
              Document stays in the ledger as rejected (history). Provide a brief reason.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRejectConfirm}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CockpitPage>
  );
}
